import { Injectable } from '@angular/core';
import { LocalStorage } from '@core/services/utils/localstorage.utils';
import { PosSettingsService } from '@core/services/system/pos-settings.service';
import { StorageKeys } from '@shared/constants';
import { LogService } from '@core/services/system/logger';
import moment from 'moment';
import { LastTransactionEntry, LastTransactionMap } from '@core/services/system/adyen/adyen-types';
import { ILogger } from '@paymash/logging';
import { AlertService } from '@core/services/system/alert.service';

@Injectable()
export class AdyenLastTransactionMapService {
  private readonly storageKey: string = StorageKeys.paymashPayLastTransactionMap;
  private readonly logger: ILogger = this.logService.createLogger('AdyenLastTransactionMapService');

  constructor(
    private readonly localStorage: LocalStorage,
    private readonly posSettingsService: PosSettingsService,
    private readonly alertService: AlertService,
    private readonly logService: LogService
  ) {}

  /**
   * Saves or updates the transaction entry for the given invoiceUuid asynchronously.
   */
  saveTransaction(invoiceUuid: string, saleID: string, serviceID: string): void {
    Promise.resolve().then(() => {
      try {
        const map: LastTransactionMap = this.getMap();
        map[invoiceUuid] = { date: new Date().toISOString(), saleID, serviceID };
        this.saveMap(map);
      } catch (error) {
        this.logger.error(error, 'saveLastTransaction');
      }
    });
  }

  /**
   * Removes the transaction entry for the given invoiceUuid asynchronously.
   */
  removeTransaction(invoiceUuid: string): void {
    Promise.resolve().then(() => {
      try {
        const map: LastTransactionMap = this.getMap();
        delete map[invoiceUuid];
        this.saveMap(map);
      } catch (error) {
        this.logger.error(error, 'removeLastTransaction');
      }
    });
  }

  /**
   * Returns true if there is an uncompleted transaction for the given invoiceUuid.
   */
  hasUncompletedTransactionInInvoice(invoiceUuid: string): boolean {
    return Boolean(this.getTransactionEntry(invoiceUuid));
  }

  /**
   * Returns the full LastTransactionEntry for the given invoiceUuid, or null if not found.
   */
  getTransactionEntry(invoiceUuid: string): LastTransactionEntry | null {
    const map: LastTransactionMap = this.getMap();
    const entry: LastTransactionEntry = map[invoiceUuid];
    return entry?.saleID && entry?.serviceID ? entry : null;
  }

  showMessageForUncompletedTransaction(): void {
    this.alertService
      .showAlert({
        header: 'paymash-pay.invoice-with-lost-transaction-alert.title',
        message: `paymash-pay.invoice-with-lost-transaction-alert.message`,
      })
      .then();
  }

  /**
   * Filters the map by the invoicesKeepDuration setting, removing all entries older than the allowed duration.
   * Runs asynchronously to avoid blocking the main thread.
   */
  cleanupExpiredTransactions(): void {
    Promise.resolve().then(() => {
      try {
        const keepDurationDays: string = this.posSettingsService.getInvoicesKeepDuration();
        const cutoffDate: moment.Moment = moment().utc().subtract(keepDurationDays, 'days').startOf('day');
        const map: LastTransactionMap = this.getMap();
        let hasChanges: boolean = false;

        for (const invoiceUuid of Object.keys(map)) {
          const entry: LastTransactionEntry = map[invoiceUuid];
          if (moment(entry.date).isBefore(cutoffDate)) {
            delete map[invoiceUuid];
            hasChanges = true;
          }
        }

        if (hasChanges) {
          this.saveMap(map);
        }
      } catch (error) {
        this.logger.error(error, 'cleanupExpiredTransactions');
      }
    });
  }

  private getMap(): LastTransactionMap {
    return this.localStorage.getObject(this.storageKey) as LastTransactionMap;
  }

  private saveMap(map: LastTransactionMap): void {
    this.localStorage.setObject(this.storageKey, map);
  }
}

